/**
 * Label Profile Modal — read-only viewer used by Finance/Founder/Admin to
 * verify a label's KYC details from inside the Invoice workflow without
 * leaving the page. Renders ALL profile fields and falls back to
 * "Not Provided" for empty values so verification gaps are obvious.
 */
import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
    Loader2, Mail, Phone, MapPin, Building2, FileText, Globe, Download, ShieldCheck,
} from 'lucide-react';
import { labelAPI } from '../lib/api';

const NotProvided = () => (
    <span className="text-muted-foreground/60 italic text-sm">Not Provided</span>
);

const Field = ({ label, value, icon: Icon }) => (
    <div className="space-y-0.5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            {Icon && <Icon className="w-3 h-3" />}
            {label}
        </div>
        <div className="text-sm font-medium break-words">
            {value ? value : <NotProvided />}
        </div>
    </div>
);

const SectionHeading = ({ children }) => (
    <h3 className="text-[11px] tracking-[0.18em] uppercase text-primary font-semibold pt-1">{children}</h3>
);

const LabelProfileModal = ({ open, onOpenChange, labelId }) => {
    const [loading, setLoading] = useState(true);
    const [label, setLabel] = useState(null);

    useEffect(() => {
        if (!open || !labelId) return;
        let cancelled = false;
        setLoading(true);
        setLabel(null);
        (async () => {
            try {
                const res = await labelAPI.getOne(labelId);
                if (!cancelled) setLabel(res.data);
            } catch {
                if (!cancelled) setLabel(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, labelId]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="label-profile-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-heading">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                        Label Profile
                    </DialogTitle>
                    <DialogDescription>
                        Read-only KYC view for invoice verification.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                ) : !label ? (
                    <div className="py-12 text-center text-sm text-muted-foreground">
                        Unable to load label profile.
                    </div>
                ) : (
                    <div className="space-y-5">
                        {/* Identity */}
                        <div className="rounded-xl border border-border/40 p-4 bg-card/40">
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <div className="text-xl font-heading font-bold">{label.name}</div>
                                    <div className="text-xs text-muted-foreground font-mono">{label.code}</div>
                                </div>
                                <Badge variant="outline" className="text-[10px] uppercase">
                                    Revenue Share: {label.revenue_share_percentage ?? 70}%
                                </Badge>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Contact Person" value={label.contact_person} icon={Building2} />
                                <Field label="Email" value={label.email} icon={Mail} />
                                <Field label="Phone" value={label.phone} icon={Phone} />
                                <Field label="Address" value={label.address} icon={MapPin} />
                            </div>
                        </div>

                        {/* Registration */}
                        <div className="rounded-xl border border-border/40 p-4 bg-card/40 space-y-3">
                            <SectionHeading>Registration / Tax</SectionHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Registered Name" value={label.registered_name} />
                                <Field label="Registered Address" value={label.registered_address} />
                                <Field label="PAN Number" value={label.pan_number} />
                                <Field label="GST Number" value={label.gst_number} />
                                <Field label="TAN Number" value={label.tan_number} />
                            </div>
                        </div>

                        {/* Contact Person */}
                        <div className="rounded-xl border border-border/40 p-4 bg-card/40 space-y-3">
                            <SectionHeading>Authorised Signatory</SectionHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Email" value={label.contact_person_email} icon={Mail} />
                                <Field label="Phone" value={label.contact_person_phone} icon={Phone} />
                            </div>
                        </div>

                        {/* Bank */}
                        <div className="rounded-xl border border-border/40 p-4 bg-card/40 space-y-3">
                            <SectionHeading>Bank Account Details</SectionHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Account Holder" value={label.bank_account_name} />
                                <Field label="Account Number" value={label.bank_account_number} />
                                <Field label="IFSC Code" value={label.bank_ifsc_code} />
                                <Field label="Account Type" value={label.bank_account_type} />
                                <Field label="Bank Name" value={label.bank_name} />
                                <Field label="Branch" value={label.bank_branch} />
                            </div>
                        </div>

                        {/* Documents */}
                        <div className="rounded-xl border border-border/40 p-4 bg-card/40 space-y-3">
                            <SectionHeading>Documents</SectionHeading>
                            {(!label.documents || label.documents.length === 0) ? (
                                <NotProvided />
                            ) : (
                                <ul className="space-y-2" data-testid="label-profile-documents">
                                    {label.documents.map((d) => (
                                        <li key={d.id} className="flex items-center justify-between gap-3 p-2 rounded-md bg-background/40 border border-border/30">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FileText className="w-4 h-4 text-secondary shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium truncate" title={d.filename}>{d.filename}</div>
                                                    <div className="text-[11px] text-muted-foreground">
                                                        {d.document_type?.replace('_', ' ').toUpperCase()}
                                                        {' • '}
                                                        {d.verified ? <span className="text-secondary">Verified</span> : <span className="text-muted-foreground">Unverified</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                asChild
                                                className="flex-shrink-0"
                                            >
                                                <a
                                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/labels/${label.id}/documents/${d.id}/download`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`label-doc-${d.id}`}
                                                >
                                                    <Download className="w-3.5 h-3.5 mr-1.5" /> View
                                                </a>
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Web presence (only show if any value) */}
                        {(label.website || label.youtube_url || label.instagram_url || label.spotify_url || label.twitter_url) && (
                            <div className="rounded-xl border border-border/40 p-4 bg-card/40 space-y-3">
                                <SectionHeading>Web</SectionHeading>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Website"   value={label.website}        icon={Globe} />
                                    <Field label="YouTube"   value={label.youtube_url}    icon={Globe} />
                                    <Field label="Instagram" value={label.instagram_url}  icon={Globe} />
                                    <Field label="Spotify"   value={label.spotify_url}    icon={Globe} />
                                    <Field label="Twitter"   value={label.twitter_url}    icon={Globe} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end pt-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-label-profile-btn">
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LabelProfileModal;
